'use client';

import { useEffect } from 'react';
import { getAbsoluteUrl } from '@/config/seo';
import {
  getJobSearchDescription,
  getJobSearchKeywords,
  getJobSearchLabel,
  getJobSearchTitle,
} from '@/config/seo-job-result-cases';
import {
  JOB_PAGE_QUERY_PARAM,
  parseJobSearchPage,
} from '@/services/jobs/constants';
import type { JobSummary } from '@/services/jobs/model';
import {
  getJobSearchCanonicalPath,
  hasIndexBlockingJobSearchParams,
} from '../job-search-seo';
import {
  getJobSearchStructuredData,
  JOB_SEARCH_STRUCTURED_DATA_SCRIPT_ID,
  serializeJobSearchStructuredData,
} from '../job-search-structured-data';

type JobSearchMetadataSyncProps = {
  hasError: boolean;
  isSearching: boolean;
  jobs: JobSummary[];
  searchLocations: string[];
  searchQuery: string;
  seoUrlStateKey: string;
  total: number;
};

export function JobSearchMetadataSync({
  hasError,
  isSearching,
  jobs,
  searchLocations,
  searchQuery,
  seoUrlStateKey,
  total,
}: JobSearchMetadataSyncProps) {
  const normalizedQuery = searchQuery.trim();
  const location = searchLocations.join(', ');
  const title = getJobSearchTitle({
    keyword: normalizedQuery,
    location,
    total,
  });
  const description = getJobSearchDescription({
    keyword: normalizedQuery,
    location,
    total,
  });
  const keywordsContent = getJobSearchKeywords({
    keyword: normalizedQuery,
    location,
  }).join(',');

  useEffect(() => {
    document.title = title;

    const metaDescription = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    const metaKeywords = document.querySelector<HTMLMetaElement>(
      'meta[name="keywords"]'
    );
    const openGraphTitle = document.querySelector<HTMLMetaElement>(
      'meta[property="og:title"]'
    );
    const openGraphDescription = document.querySelector<HTMLMetaElement>(
      'meta[property="og:description"]'
    );
    const openGraphUrl = document.querySelector<HTMLMetaElement>(
      'meta[property="og:url"]'
    );
    const twitterTitle = document.querySelector<HTMLMetaElement>(
      'meta[name="twitter:title"]'
    );
    const twitterDescription = document.querySelector<HTMLMetaElement>(
      'meta[name="twitter:description"]'
    );
    const canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const robots = document.querySelector<HTMLMetaElement>(
      'meta[name="robots"]'
    );
    const searchParams = new URL(window.location.href).searchParams;
    const page = parseJobSearchPage(
      searchParams.get(JOB_PAGE_QUERY_PARAM) ?? undefined
    );
    const shouldNoIndex = hasIndexBlockingJobSearchParams(searchParams.keys());
    const canonicalPath = getJobSearchCanonicalPath({
      page,
      pathname: window.location.pathname,
      shouldNoIndex,
    });
    const canonicalUrl = getAbsoluteUrl(canonicalPath);
    const shouldIndex =
      !shouldNoIndex &&
      !isSearching &&
      !hasError &&
      total > 0 &&
      jobs.length > 0;
    const currentStructuredData = document.getElementById(
      JOB_SEARCH_STRUCTURED_DATA_SCRIPT_ID
    );

    if (metaDescription) metaDescription.content = description;
    if (metaKeywords) metaKeywords.content = keywordsContent;
    if (openGraphTitle) openGraphTitle.content = title;
    if (openGraphDescription) openGraphDescription.content = description;
    if (openGraphUrl) openGraphUrl.content = canonicalUrl;
    if (twitterTitle) twitterTitle.content = title;
    if (twitterDescription) twitterDescription.content = description;
    if (canonical) canonical.href = canonicalUrl;
    if (robots) {
      robots.content = shouldIndex ? 'index, follow' : 'noindex, follow';
    }

    if (!shouldIndex) {
      if (currentStructuredData instanceof HTMLScriptElement) {
        currentStructuredData.remove();
      }

      return;
    }

    const structuredData = getJobSearchStructuredData({
      breadcrumbName: getJobSearchLabel({
        keyword: normalizedQuery,
        location,
      }),
      canonicalPath,
      description,
      jobs,
      name: title,
      total,
    });
    const structuredDataScript =
      currentStructuredData instanceof HTMLScriptElement
        ? currentStructuredData
        : document.createElement('script');
    const didCreateStructuredDataScript = !structuredDataScript.isConnected;

    structuredDataScript.id = JOB_SEARCH_STRUCTURED_DATA_SCRIPT_ID;
    structuredDataScript.type = 'application/ld+json';
    structuredDataScript.textContent =
      serializeJobSearchStructuredData(structuredData);

    if (didCreateStructuredDataScript) {
      document.head.appendChild(structuredDataScript);
    }

    return () => {
      if (didCreateStructuredDataScript) structuredDataScript.remove();
    };
  }, [
    description,
    hasError,
    isSearching,
    jobs,
    keywordsContent,
    location,
    normalizedQuery,
    seoUrlStateKey,
    title,
    total,
  ]);

  return null;
}
